/**
 * NotificationDropdown Component
 * Modern, redesigned notification dropdown with real backend integration
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  Check,
  Loader2,
  BellOff,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useNotificationContext } from '@/contexts/NotificationContext'
import type { Notification, NotificationType } from '@/types/notifications'
import { useLocalization } from '@/contexts/LocalizationContext'

// Maximum notifications to show in dropdown
const MAX_DROPDOWN_NOTIFICATIONS = 5

/**
 * Get icon component based on notification type
 */
function getNotificationIcon(type: NotificationType) {
  const iconClass = 'h-4 w-4 flex-shrink-0'
  
  switch (type) {
    case 'success':
      return <CheckCircle2 className={cn(iconClass, 'text-emerald-500')} />
    case 'warning':
      return <AlertTriangle className={cn(iconClass, 'text-amber-500')} />
    case 'error':
      return <XCircle className={cn(iconClass, 'text-red-500')} />
    default:
      return <Info className={cn(iconClass, 'text-blue-500')} />
  }
}

/**
 * Get badge styles based on notification type
 */
function getTypeBadgeStyles(type: NotificationType): string {
  switch (type) {
    case 'success':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case 'warning':
      return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'error':
      return 'bg-red-50 text-red-700 border-red-200'
    default:
      return 'bg-blue-50 text-blue-700 border-blue-200'
  }
}

/**
 * Determine navigation path based on notification content
 */
function getNavigationPath(notification: Notification): string {
  const title = notification.title.toLowerCase()
  const body = notification.body.toLowerCase()
  const content = `${title} ${body}`

  if (content.includes('order')) return '/orders'
  if (content.includes('provider')) return '/providers'
  if (content.includes('payout') || content.includes('financial') || content.includes('invoice'))
    return '/financials'
  if (content.includes('user')) return '/users'
  if (content.includes('account') || content.includes('setting'))
    return '/account-settings'
  
  return '/dashboard'
}

/**
 * Individual notification item in the dropdown
 */
function NotificationItem({
  notification,
  onMarkAsRead,
  onNavigate,
}: {
  notification: Notification
  onMarkAsRead: (id: number) => void
  onNavigate: (path: string) => void
}) {
  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id)
    }
    onNavigate(getNavigationPath(notification))
  }

  const timeAgo = formatDistanceToNow(notification.createdAt, { addSuffix: true })

  return (
    <button
      onClick={handleClick}
      className={cn(
        'w-full text-start p-3 transition-colors hover:bg-muted/50 focus:bg-muted/50 focus:outline-none',
        !notification.isRead && 'bg-primary/5'
      )}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className="pt-0.5">
          {getNotificationIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <p
              className={cn(
                'text-sm leading-tight truncate',
                !notification.isRead ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'
              )}
            >
              {notification.title}
            </p>
            {!notification.isRead && (
              <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1" />
            )}
          </div>
          
          <p className="text-xs text-muted-foreground line-clamp-2">
            {notification.body}
          </p>
          
          <div className="flex items-center gap-2 pt-1">
            <Badge
              variant="outline"
              className={cn('text-[10px] px-1.5 py-0 h-4 capitalize', getTypeBadgeStyles(notification.type))}
            >
              {notification.channel}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {timeAgo}
            </span>
          </div>
        </div>
      </div>
    </button>
  )
}

/**
 * Empty state when no notifications
 */
function EmptyState() {
  const { t } = useLocalization()
  
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <BellOff className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground">{t('notifications.noNotifications')}</p>
      <p className="text-xs text-muted-foreground text-center mt-1">
        {t('notifications.caughtUpCheckLater')}
      </p>
    </div>
  )
}

/**
 * Loading state
 */
function LoadingState() {
  const { t } = useLocalization()
  
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mb-2" />
      <p className="text-xs text-muted-foreground">{t('notifications.loadingNotifications')}</p>
    </div>
  )
}

/**
 * Main NotificationDropdown Component
 */
export function NotificationDropdown() {
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    hasUnreadNotifications,
    loading,
    markAsRead,
    markAllAsRead,
    isDropdownOpen,
    setDropdownOpen,
  } = useNotificationContext()

  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false)
  const { t } = useLocalization()

  // Get notifications to display (limited for dropdown)
  const displayNotifications = notifications.slice(0, MAX_DROPDOWN_NOTIFICATIONS)
  const hasMoreNotifications = notifications.length > MAX_DROPDOWN_NOTIFICATIONS

  const handleMarkAsRead = useCallback(
    async (id: number) => {
      try {
        await markAsRead(id)
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    },
    [markAsRead]
  )

  const handleMarkAllAsRead = useCallback(async () => {
    setIsMarkingAllRead(true)
    try {
      await markAllAsRead()
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    } finally {
      setIsMarkingAllRead(false)
    }
  }, [markAllAsRead])

  const handleNavigate = useCallback(
    (path: string) => {
      setDropdownOpen(false)
      navigate(path)
    },
    [navigate, setDropdownOpen]
  )

  const handleViewAll = useCallback(() => {
    setDropdownOpen(false)
    navigate('/notifications')
  }, [navigate, setDropdownOpen])

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 hover:bg-muted"
          aria-label={`Notifications${hasUnreadNotifications ? ` (${unreadCount} unread)` : ''}`}
        >
          <Bell className="h-5 w-5" />
          {hasUnreadNotifications && (
            <Badge
              className={cn(
                'absolute -top-1 -end-1 h-5 min-w-5 flex items-center justify-center p-0 text-[10px] font-semibold',
                'bg-red-500 text-white border-2 border-background hover:bg-red-500'
              )}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-80 sm:w-96 p-0 overflow-hidden"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm">{t('notifications.title')}</h3>
            {hasUnreadNotifications && (
              <Badge variant="secondary" className="bg-primary/10 text-primary text-[10px] px-1.5">
                {unreadCount} {t('notifications.new')}
              </Badge>
            )}
          </div>
          {hasUnreadNotifications && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              onClick={handleMarkAllAsRead}
              disabled={isMarkingAllRead}
            >
              {isMarkingAllRead ? (
                <Loader2 className="h-3 w-3 animate-spin me-1" />
              ) : (
                <Check className="h-3 w-3 me-1" />
              )}
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>

        <Separator />

        {/* Notification List */}
        <ScrollArea className="max-h-[400px]">
          {loading ? (
            <LoadingState />
          ) : displayNotifications.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="divide-y divide-border">
              {displayNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {displayNotifications.length > 0 && (
          <>
            <Separator />
            <div className="p-2 bg-muted/30">
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-8 text-xs font-medium text-primary hover:text-primary hover:bg-primary/10"
                onClick={handleViewAll}
              >
                {hasMoreNotifications
                  ? t('notifications.viewAllNotificationsCount', { count: notifications.length })
                  : t('notifications.viewAllNotifications')}
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
